import { useMemo } from 'react';
import type { FUEL_TYPE_OPTION } from '@/shared/consts';
import { parseStation, type Station } from './station.schema';
import type { StationsInScreen } from './station.types';
import useStationsAround from './useStationsAround';
import useStationsInScreenBound from './useStationsInScreenBound';

type StationInScreen = NonNullable<StationsInScreen[number]>;

const hasCoords = (
	station: StationInScreen,
): station is StationInScreen & { coor_x: number; coor_y: number } =>
	station.coor_x !== null && station.coor_y !== null;

const useStations = (
	fuelType: (typeof FUEL_TYPE_OPTION)[number]['value'],
): Station[] => {
	const stationsInScreenBound = useStationsInScreenBound();
	const stationsAround = useStationsAround(fuelType);

	const stations = useMemo(() => {
		const stationsInScreenMap = new Map(
			stationsInScreenBound
				.filter(hasCoords)
				.map((stationInScreen) => [stationInScreen.id, stationInScreen]),
		);

		return stationsAround
			.filter(
				(s): s is NonNullable<typeof s> =>
					s !== null && stationsInScreenMap.has(s.UNI_ID),
			)
			.map((stationAround) => {
				const stationInScreen = stationsInScreenMap.get(stationAround.UNI_ID);
				if (!stationInScreen) return null;

				const parsedStation = parseStation({
					id: stationAround.UNI_ID,
					stationName: stationAround.OS_NM,
					address: stationInScreen.road_number_address ?? '',
					price: stationAround.PRICE,
					distance: stationAround.DISTANCE,
					coorX: stationInScreen.coor_x,
					coorY: stationInScreen.coor_y,
					stationLogo: stationInScreen.station_logo,
				});

				if (!parsedStation.isOk()) {
					return null;
				}

				return parsedStation.value;
			})
			.filter((s): s is Station => s !== null);
	}, [stationsInScreenBound, stationsAround]);

	return stations;
};

export default useStations;
